<template>
    <div v-if="filters.group_ids.length === 0">
        <PanelPage>
            <form action="" @submit.prevent="selectGroups">
                <GroupSelector
                    :grade-levels="options.gradeLevels"
                    :user-grade-levels="meta.preferences.grade_level_ids"
                >
                    <template v-slot:default="{ filteredGradeLevels }">
                        <div class="mb-2 flex gap-x-2" v-for="gradeLevel in filteredGradeLevels" :key="gradeLevel.id">
                            <label v-for="group in gradeLevel.groups" :key="group.id">
                                <input
                                    type="checkbox"
                                    name="group_ids[]"
                                    v-model="groupsForm.group_ids"
                                    :value="group.id"
                                    :id="`group_` + group.id"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2"
                                >
                                    {{ group.name }}
                                </span>
                            </label>
                        </div>
                    </template>
                </GroupSelector>

                <div class="flex justify-end">
                    <SubmitFormButton :button-text="$t('messages.continue')" :processing="groupsForm.processing" />
                </div>
            </form>
        </PanelPage>
    </div>
    <div v-else class="pt-2 pb-6 md:py-6">
        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
                <div class="md:grid md:grid-cols-8 md:gap-6">
                    <div class="md:col-span-3">
                        <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow mt-5">
                            <div class="p-4">
                                <StudentRelativesSelector :groups="groups" v-model="form.recipients_ids" />
                            </div>
                        </div>
                    </div>
                    <div class="md:col-span-5">
                        <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow mt-5">
                            <div class="p-4">
                                <div class="mt-3">
                                    <span
                                        class="relative z-0 inline-flex shadow-sm rounded-md sm:shadow-none sm:space-x-3"
                                    >
                                        <span class="flex space-x-3">
                                            <DropdownButton class="">
                                                <template v-slot:text>
                                                    <svg
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        class="clipboard-check mr-3 w-4 h-4 inline-block text-gray-400"
                                                    >
                                                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                    {{ $tc('messages.templates', 2) }}
                                                </template>
                                                <template v-slot:content>
                                                    <button
                                                        type="button"
                                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                                        role="menuitem"
                                                        v-for="messageTemplate in options.templates"
                                                        @click="prependToMessage(messageTemplate)"
                                                        :key="messageTemplate.id"
                                                    >
                                                        {{ messageTemplate.title }}
                                                    </button>
                                                </template>
                                            </DropdownButton>
                                        </span>
                                    </span>
                                </div>

                                <div class="">
                                    <FormLabel :value="$t('messages.message')" for="message" />
                                    <Editor id="message" v-model="form.message" name="message" class="mt-2" />
                                    <FormError :value="form.errors.message" class="mt-2" />
                                </div>

                                <div>
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.image')" for="image" />
                                        <input
                                            id="image"
                                            ref="imageFileInput"
                                            @input="handleImageInput"
                                            name="image"
                                            type="file"
                                            accept="image/*"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.image" class="mt-2" />
                                    </div>
                                    <div class="w-1/2 relative" v-if="preview">
                                        <div class="absolute w-full top-0 right-0">
                                            <button type="button" @click="removeImage">
                                                <svg
                                                    class="w-6 h-6 text-red-300"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </div>
                                        <img :src="preview" alt="" />
                                    </div>
                                </div>

                                <div>
                                    <div>
                                        {{ $tc('messages.attachments', 2) }}
                                    </div>
                                    <div>
                                        <div class="mt-1" v-if="form.attachments.length < 3">
                                            <FormLabel :value="$tc('messages.attachments', 1)" for="attachment" />
                                            <input
                                                id="attachment"
                                                ref="attachmentFileInput"
                                                @input="handleNewAttachment"
                                                name="attachment"
                                                type="file"
                                                accept="application/pdf,.jpg,.jpeg"
                                                class="mt-1 w-full"
                                            />
                                            <FormError :value="form.errors.attachments" class="mt-2" />
                                        </div>
                                    </div>
                                    <div class="mt-1">
                                        <AttachmentsUploadPreviews
                                            @remove="removeAttachment"
                                            :attachments="form.attachments"
                                            :max-bytes-size="12 * 1024 * 1024"
                                        />
                                    </div>
                                </div>

                                <div class="w-full flex items-center mt-3 gap-x-2">
                                    <AppButton
                                        element="a"
                                        :href="type.url"
                                        :key="type.url"
                                        v-for="type in meta.links.load_payment_types_links"
                                    >
                                        {{ type.text }}
                                    </AppButton>
                                </div>

                                <div v-auto-animate>
                                    <AlertMessage class="mt-3 inline-block" v-if="exceedsMaxFileSize">
                                        <template v-slot:title>
                                            {{ $t('messages.warning') }}
                                        </template>
                                        <template v-slot:body>
                                            <div>
                                                {{ $t('messages.exceeds_combined_max_file_size') }}
                                            </div>
                                        </template>
                                    </AlertMessage>
                                </div>

                                <div class="w-full flex justify-end items-center mt-3">
                                    <SubmitFormButton
                                        :button-text="$t('messages.send')"
                                        :processing="form.processing"
                                        :disabled="exceedsMaxFileSize"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import StudentRelativesSelector from '@/components/pageSpecific/StudentRelativesSelector.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import AppButton from '@/components/AppButton.vue';
import AttachmentsUploadPreviews from '@/components/AttachmentsUploadPreviews.vue';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';
import GroupSelector from '@/components/GroupSelector.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    components: {
        AlertMessage,
        PanelPage,
        GroupSelector,
        UnsavedChangesFormWarning,
        Editor,
        AttachmentsUploadPreviews,
        AppButton,
        StudentRelativesSelector,
        FormLabel,
        FormError,
        SubmitFormButton,
        DropdownButton,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'filters', 'groups', 'recipientIds', 'options'],
    data() {
        return {
            preview: null,
            form: this.$inertia.form({
                message: '',
                recipients_ids: this.recipientIds,
                image: null,
                attachments: [],
            }),
            groupsForm: this.$inertia.form({
                group_ids: [],
            }),
        };
    },
    computed: {
        totalSize: function () {
            const imageSize = this.form.image ? this.form.image.size : 0;
            return this.form.attachments.reduce((total, attachment) => total + attachment.size, imageSize);
        },
        exceedsMaxFileSize: function () {
            return this.totalSize > 23 * 1024 * 1024;
        },
    },
    methods: {
        prependToMessage(messageTemplate) {
            this.form.message = messageTemplate.body + '\n\n' + this.form.message;
        },
        submit() {
            this.form.post(this.meta.links.store);
        },
        selectGroups() {
            this.groupsForm.get(this.meta.links.create);
        },
        handleImageInput(event) {
            const file = event.target.files[0];
            this.form.image = file;
            const reader = new FileReader();
            reader.addEventListener('load', (event) => (this.preview = event.target.result));
            reader.readAsDataURL(file);
            this.$refs.imageFileInput.value = null;
        },
        removeImage() {
            this.preview = null;
            this.form.image = null;
        },
        handleNewAttachment(event) {
            const file = event.target.files[0];
            this.form.attachments.push(file);
            this.$refs.attachmentFileInput.value = null;
        },
        removeAttachment(index) {
            this.form.attachments.splice(index, 1);
        },
    },
};
</script>
